/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Editor } from "@tiptap/react";
import React, { useEffect } from "react";
import { toast } from "react-toastify";
import { useRecoilState, useRecoilValue, useResetRecoilState } from "recoil";
import {
  DelayTimeAtom,
  DelayTimeIsOverAtom,
  DelayTimeToEndAtom,
} from "../../atoms/DelayTimeAtom";
import { EmailMode } from "../../atoms/EmailAtom";
import { ScheduleDateAtom } from "../../atoms/ScheduleDateAtom";
import useSetEmail from "../../hook/useSetEmail";
import { getNewTime } from "../../pages/new-email";
import { localStorageRemoveItem } from "../../utils/LocalStorage";
import IconBerserkerMail from "../Icon";

const ToastContainer = styled.div`
  display: flex;
  align-items: center;
  background: #333333;
  border-radius: 2px;
  width: 100%;
  height: 100%;
  padding: 0px 10px;
`;

const TimerContainer = styled.div`
  display: flex;
  width: 100%;
`;

const Content = styled.div`
  color: #fff;
`;

const TimerValue = styled.div`
  color: #fb972e;
`;
const Options = styled.div`
  display: flex;
  justify-content: flex-end;
`;
const Action = styled.div`
  color: #fb972e;
  margin: 0px 10px;
  cursor: pointer;

  :first-of-type {
    padding-left: 5px;
    margin-left: 5px;
  }
`;

const EmailSentContainer = styled.div`
  display: flex;
  align-items: center;
`;

const CloseIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 5px;
  z-index: 15px;
  > div {
    cursor: pointer;
  }
`;

const ToastContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const StyledLoading = styled.div`
  border: 3px solid #facb99;
  border-top: 3px solid #fb972e;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  animation: spin 1s linear infinite;
  margin-right: 5px;
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

interface Props {
  cancelEmail: () => void;
  viewEmail: () => void;
  editor: Editor;
  mode: EmailMode;
}

const EXTRA_DELAY_PREPARE_TO_SEND = 3;

export default function DelayBroadcastEmailToast(props: Props) {
  const { cancelEmail, viewEmail, editor, mode } = props;
  const delayTime = useRecoilValue(DelayTimeAtom);
  const [delayTimeToEnd, setDelayTimeToEnd] =
    useRecoilState(DelayTimeToEndAtom);
  const [delayTimeIsOver, setDelayTimeIsOver] =
    useRecoilState(DelayTimeIsOverAtom);
  const [prepareToSend, setPrepareToSend] = React.useState(false);
  const resetScheduleDate = useResetRecoilState(ScheduleDateAtom);
  const { resetEmailContent } = useSetEmail(mode, editor);

  useEffect(() => {
    const interval = setInterval(() => {
      const time = getNewTime(delayTime);
      setDelayTimeToEnd(time);
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (!delayTimeToEnd) return;

    if (delayTimeToEnd.min === 0 && delayTimeToEnd.sec <= 0) {
      setPrepareToSend(true);
    }

    if (
      delayTimeToEnd.min === 0 &&
      delayTimeToEnd.sec <= 0 - EXTRA_DELAY_PREPARE_TO_SEND
    ) {
      setPrepareToSend(false);
      setDelayTimeIsOver(true);
      resetEmailContent.current();
      resetScheduleDate();
    }
  }, [delayTimeToEnd]);

  return (
    <ToastContainer>
      <ToastContent>
        {!prepareToSend && !delayTimeIsOver && (
          <TimerContainer>
            <IconBerserkerMail
              name="stopwatch"
              width={16}
              height={16}
              color="#FB972E"
            />
            <Content
              css={css`
                padding: 0px 5px;
              `}
            >
              Sending in
            </Content>
            <TimerValue>
              {delayTimeToEnd?.min.toLocaleString("en-US", {
                minimumIntegerDigits: 2,
              })}
              :
              {delayTimeToEnd?.sec.toLocaleString("en-US", {
                minimumIntegerDigits: 2,
              })}
            </TimerValue>
          </TimerContainer>
        )}
        {delayTimeIsOver && !prepareToSend && (
          <EmailSentContainer>
            <Content>Email Broadcast Sent! </Content>
            <IconBerserkerMail
              name="check"
              width={15}
              height={15}
              color="#75B85C"
            />
          </EmailSentContainer>
        )}
        {prepareToSend && (
          <div
            css={css`
              display: flex;
              align-items: center;
            `}
          >
            <StyledLoading />
            <span style={{ color: "#fff" }}>Preparing to send…</span>
          </div>
        )}
        <Options>
          <Action onClick={viewEmail}>View</Action>
          {!delayTimeIsOver && <Action onClick={cancelEmail}>Cancel</Action>}
        </Options>
      </ToastContent>
      <CloseIconContainer onClick={() => toast.dismiss()}>
        <IconBerserkerMail
          name="close-modal"
          width={12}
          height={12}
          color="#fff"
        />
      </CloseIconContainer>
    </ToastContainer>
  );
}
